import { Bot, Shapes, Sparkles } from 'lucide-react';
import { TASK_AI_BUILDER_NAME, type TaskLabel, type TaskMember, type TaskType } from '@shared/types/tasks';
import { Badge } from '@/components/ui/badge';
import { CHIP_COLOURS } from '@/components/ui/chip-colours';
import { cn } from '@/lib/utils';

/**
 * Label chips and member names for tasks (their avatars are MemberAvatar, in components/team). Kept
 * free of the router and stores, because the Chrome extension (extension/) imports this file as well
 * as the app.
 */

export function memberName(member: TaskMember | undefined | null): string {
  if (!member) return 'Someone';
  return member.displayname?.trim() || member.email;
}

/** Their handle (as in the Inbox), else their name: for chat, activity and mentions, where it's read often. */
export function memberHandle(member: TaskMember | undefined | null): string {
  return member?.handle?.trim() || memberName(member);
}

/** A task label: a square chip in the colour picked for it (the one chip palette). */
export function LabelChip({ label, className }: { label: TaskLabel; className?: string }) {
  return (
    <Badge shape="square" size="sm" colour={CHIP_COLOURS.includes(label.color) ? label.color : 'gray'} className={cn('max-w-full', className)}>
      <span className="truncate">{label.name}</span>
    </Badge>
  );
}

/**
 * A task's type (T-331): a chip in the type's colour, with the shapes mark so it reads apart from
 * the labels. `suggested`: Jev chose it and nobody has confirmed it yet (a sparkle instead).
 */
export function TypeChip({ type, suggested, className }: { type: Pick<TaskType, 'name' | 'color'>; suggested?: boolean; className?: string }) {
  return (
    <Badge
      shape="square"
      size="sm"
      colour={CHIP_COLOURS.includes(type.color) ? type.color : 'gray'}
      className={cn('max-w-full', className)}
      data-hint={suggested ? `${type.name}, suggested by Jev` : undefined}
    >
      {suggested ? <Sparkles aria-label="Suggested by Jev" /> : <Shapes aria-hidden />}
      <span className="truncate">{type.name}</span>
    </Badge>
  );
}

const AI_AVATAR_SIZES = { xs: 'size-5 [&_svg]:size-3', sm: 'size-6 [&_svg]:size-3.5' } as const;

/** The AI builder (T-331), where a person's avatar would be: Waiting on, the card's faces. */
export function AiBuilderAvatar({ size = 'sm', ring = false, className }: { size?: keyof typeof AI_AVATAR_SIZES; ring?: boolean; className?: string }) {
  return (
    <span
      data-hint={TASK_AI_BUILDER_NAME}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-ai-border bg-ai-soft text-ai-strong',
        AI_AVATAR_SIZES[size],
        ring && 'ring-2 ring-background',
        className
      )}
    >
      <Bot aria-label={TASK_AI_BUILDER_NAME} />
    </span>
  );
}
